import { useEffect, useRef, useState } from "react";

export default function useRecorder() {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [durationSeconds, setDurationSeconds] = useState(0);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      if (audioURL) URL.revokeObjectURL(audioURL);

      if (audioContextRef.current) {
        try {
          if (audioContextRef.current.state !== "closed") {
            audioContextRef.current.close();
          }
        } catch (e) {}
      }
    };
  }, [audioURL]);

  async function startRecording() {
    if (isRecording) return; 
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    streamRef.current = stream;
    chunksRef.current = [];

    const recorder = new MediaRecorder(stream);
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data?.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      setAudioBlob(blob);

      if (audioURL) URL.revokeObjectURL(audioURL);

      const url = URL.createObjectURL(blob);
      setAudioURL(url);

      // Compute duration via AudioContext
      try {
        const arrayBuffer = await blob.arrayBuffer();
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioCtx;

        const decoded = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
        const d = Math.round(decoded.duration);
        setDurationSeconds(d);
      } catch (err) {
        setDurationSeconds(0);
      }
    };

    recorder.start(1000);
    setIsRecording(true);
  }

  function stopRecording() {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    setIsRecording(false);
  }

  function reset() {
    setAudioBlob(null);
    setAudioURL(null);
    setDurationSeconds(0);
    chunksRef.current = [];
  }

  return {
    startRecording,
    stopRecording,
    reset,
    isRecording,
    audioBlob,
    audioURL,
    durationSeconds,
  };
}